import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAccountLookup, useStreemLogin, useGetSSORedirectUrl } from "../lib/api";
import { usePasswordResetRequest } from "../lib/api-central-mu";
import { AccountType } from "../types/auth";
import { encodeCredential } from "../utils/encryption";
import { useAuthStore } from "../store/useAuthStore";
import { Eye, EyeOff, ArrowRight, GitPullRequestArrow, ChevronLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "../lib/use-toast";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/** Microsoft four-square brand mark. */
const MicrosoftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#7fba00" d="M12 1h10v10H12z" />
    <path fill="#00a4ef" d="M1 12h10v10H1z" />
    <path fill="#ffb900" d="M12 12h10v10H12z" />
  </svg>
);

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false)
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [ssoRedirectUrl, setSsoRedirectUrl] = useState<string | null>(null);
  const [step, setStep] = useState<"username" | "password" | "sso">("username");
  const [forgotOpen, setForgotOpen] = useState(false);

  const { mutateAsync: lookupAccount, isPending: isLookingUp } = useAccountLookup();
  const { mutateAsync: login, isPending: isLoggingIn } = useStreemLogin();
  const { mutateAsync: getSSORedirectUrl, isPending: isLoadingSSOUrl } = useGetSSORedirectUrl();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/workflows");
    }
  }, [isAuthenticated, navigate]);

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) return;

    try {
      const response = await lookupAccount({ username });

      if (response.data) {
        setAccountType(response.data.type);

        if (response.data.type === AccountType.LOCAL || response.data.type === AccountType.DEFAULT_USER) {
          // LOCAL / DEFAULT_USER authentication - show password field
          setStep("password");
        } else {
          // Any non-LOCAL type is treated as SSO (AZURE, OKTA, SAML, etc.)
          // Get SSO redirect URL from backend (uses utility_settings)
          try {
            const state = Math.random().toString(36).substring(7);
            const ssoResponse = await getSSORedirectUrl({ state, username });
            if (ssoResponse.data && ssoResponse.data.redirectUrl) {
              setSsoRedirectUrl(ssoResponse.data.redirectUrl);
              setStep("sso");
            } else {
              throw new Error("Failed to get SSO redirect URL");
            }
          } catch (ssoError) {
            console.error("SSO redirect URL error:", ssoError);
            toast({
              variant: "destructive",
              title: "SSO Setup Error",
              description: "Failed to initialize SSO login. Please try again.",
            });
          }
        }
      } else if (response.errors && response.errors.length > 0) {
        toast({
          variant: "destructive",
          title: "Account Lookup Failed",
          description: response.errors[0].message,
        });
      }
    } catch (error: any) {
      console.error("Account lookup error:", error);

      // Try to extract error message from the response
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to lookup account. Please try again.";

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) return;

    try {
      const encodedPassword = encodeCredential(password);
      const response = await login({
        username,
        password: encodedPassword,
        userType: accountType ?? undefined,
      });

      if (response.data) {
        // Login successful, user will be redirected by auth store
        navigate("/workflows");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        "Invalid credentials. Please try again.";
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: errorMessage,
      });
    }
  };

  const handleSSORedirect = () => {
    if (ssoRedirectUrl) {
      // Store username in auth store for SSO callback page (persisted to localStorage)
      useAuthStore.getState().setPendingUsername(username);
      // Redirect to SSO provider - will callback to /sso/auth
      window.location.href = ssoRedirectUrl;
    }
  };

  // Direct "Sign in with Microsoft" shortcut from the username step. No email is required — the
  // backend resolves the identity from Microsoft and matches it against allowed_users. If an email
  // was typed, it's passed along as a login hint.
  const handleMicrosoftSignIn = async () => {
    try {
      const hint = username.trim();
      const state = Math.random().toString(36).substring(7);
      const ssoResponse = await getSSORedirectUrl({ state, username: hint });
      if (ssoResponse.data?.redirectUrl) {
        useAuthStore.getState().setPendingUsername(hint || null);
        window.location.href = ssoResponse.data.redirectUrl;
      } else {
        throw new Error("Failed to get SSO redirect URL");
      }
    } catch (error: any) {
      console.error("Microsoft sign-in error:", error);
      toast({
        variant: "destructive",
        title: "SSO Setup Error",
        description:
          error?.response?.data?.errors?.[0]?.message ||
          error?.message ||
          "Failed to initialize Microsoft sign-in. Please try again.",
      });
    }
  };

  const handleBack = () => {
    setStep("username");
    setPassword("");
    setAccountType(null);
    setSsoRedirectUrl(null);
  };

  const isLoading = isLookingUp || isLoggingIn || isLoadingSSOUrl;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-2 mb-1">
            <GitPullRequestArrow className="w-5 h-5 text-foreground" />
            <h1 className="text-lg font-semibold text-foreground">Migration Utility</h1>
          </div>
          <p className="text-sm text-muted-foreground">Cross-instance data sync</p>
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-xl border border-border shadow-sm p-6">
          {step === "username" && (
            <form onSubmit={handleUsernameSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-foreground mb-1.5">
                  Username or Email
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUsernameSubmit(e)}
                  placeholder="Write your Username or Email ID here"
                  className="w-full px-3 py-2.5 text-sm bg-background text-foreground border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none transition-all placeholder:text-muted-foreground"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={!username.trim() || isLoading}
                className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-2 text-xs text-muted-foreground">or</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleMicrosoftSignIn}
                disabled={isLoading}
                className="w-full py-2.5 px-4 bg-card hover:bg-accent border border-border text-foreground text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MicrosoftIcon className="w-4 h-4" />
                Sign in with Microsoft
              </button>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-border">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <span className="text-sm text-muted-foreground">{username}</span>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit(e)}
                    placeholder="Enter your password"
                    className="w-full px-3 py-2.5 pr-10 text-sm bg-background text-foreground border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none transition-all placeholder:text-muted-foreground"
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!password.trim() || isLoading}
                className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  'Sign in'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {step === "sso" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-border">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <span className="text-sm text-muted-foreground">{username}</span>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  You will be redirected to SSO provider to sign in.
                </p>
              </div>

              <button
                onClick={handleSSORedirect}
                disabled={!ssoRedirectUrl}
                className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MicrosoftIcon className="w-4 h-4" />
                Sign in with SSO provider
              </button>
            </div>
          )}
        </div>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          {forgotOpen ? (
            <ForgotPasswordDialog
              initialIdentifier={username}
              onDone={() => setForgotOpen(false)}
            />
          ) : null}
        </Dialog>
      </div>

      {/* Footer - Fixed Bottom Right */}
      <div className="fixed bottom-6 right-6">
        <div className="text-xs text-muted-foreground mb-2 text-right">A Product By
        <a href="https://www.leucinetech.com" target="_blank" rel="noopener noreferrer">
          <img
            src="data:image/svg+xml;base64,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"
            width="110"
            alt="Leucine"
            className="inline-block dark:brightness-0 dark:invert"
          />
        </a>
        </div>
      </div>
    </div>
  );
};

/**
 * Self-service password recovery via email: always answers with the same generic message
 * (no user enumeration).
 */
function ForgotPasswordDialog({
  initialIdentifier,
}: {
  initialIdentifier: string;
  onDone: () => void;
}) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const { mutate: requestReset, isPending: requesting } = usePasswordResetRequest();

  const submitEmailRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    requestReset(
      { identifier: identifier.trim() },
      {
        onSuccess: (result) => setSentMessage(result.message),
        onError: () =>
          setSentMessage("If that account exists, a reset link has been sent to its email."),
      }
    );
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Reset your password</DialogTitle>
        <DialogDescription>
          Get a one-time link to set a new password for your Migration Utility account.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submitEmailRequest} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="forgot-identifier">Username or email</Label>
          <Input
            id="forgot-identifier"
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
              setSentMessage(null);
            }}
            placeholder="Your MU username or email"
            autoFocus
          />
        </div>
        <p className="text-sm text-muted-foreground">
          We'll email a one-time reset link to the address on your account.
        </p>
        {sentMessage ? (
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-500">
            <CheckCircle2 className="size-4 shrink-0 mt-0.5" />
            <span>{sentMessage}</span>
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={!identifier.trim() || requesting}>
          {requesting ? <Loader2 className="size-4 animate-spin" /> : null}
          Send reset link
        </Button>
      </form>
    </DialogContent>
  );
}

export default LoginPage;
